'use client';
import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { BaseFillThumbdown } from '@/app/components/icons/BaseFillThumbdown';
import { BaseFillThumbup } from '@/app/components/icons/BaseFillThumbup';
import { BaseLineThumbdown } from '@/app/components/icons/BaseLineThumbdown';
import { BaseLineThumbup } from '@/app/components/icons/BaseLineThumbup';
import { useCommunityInteraction } from '@/app/components/shared/Community';
import { DropdownMenuItem } from '@/app/components/ui/dropdown-menu';
import type { ReportParams, VoteType } from '@/app/types';
import { cn, reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';

interface VoteButtonProps {
  className?: string;
  id: string;
  vote: VoteType;
  onVote?: (vote?: VoteType) => void;

  // 用于上报事件
  reportParams?: ReportParams;
}

export const VoteButton: FC<VoteButtonProps> = ({ className, id, vote, onVote, reportParams }) => {
  const t = useTranslations();
  const pathname = usePathname();
  const {
    vote: updateVote,
    unVote: updateUnVote,
    loading,
  } = useCommunityInteraction({
    onInteractionStatusChange: ({ voteType, voted }) => {
      onVote?.(voted ? voteType : undefined);
    },
  });

  const handleClick = (type: 'like' | 'dislike') => (e: React.MouseEvent) => {
    // Prevent dropdown from closing
    e.preventDefault();
    e.stopPropagation();

    if (loading) return;
    if (vote === type) {
      updateUnVote({
        id,
        obj_type: 'creation',
        voteType: type,
      });
      reportEvent('interaction', {
        interaction_type: `cancel_${type}`,
        interaction_position: pathname,
        task_id: '',
        creation_id: id,
        content: '',
        ...reportParams,
      });
    } else {
      updateVote({
        id,
        obj_type: 'creation',
        voteType: type,
      });
      reportEvent('interaction', {
        interaction_type: type,
        interaction_position: pathname,
        task_id: '',
        creation_id: id,
        content: '',
        ...reportParams,
      });
    }
  };

  return (
    <>
      <DropdownMenuItem
        className={cn('cursor-pointer text-sm md:text-xs', className)}
        onClick={(e) => {
          handleClick('like')(e);
        }}
        disabled={loading}
      >
        <span className="mr-2 shrink-0 text-lg md:text-base">
          {vote === 'like' ? <BaseFillThumbup /> : <BaseLineThumbup />}
        </span>
        {t('creation.action.like')}
      </DropdownMenuItem>
      <DropdownMenuItem
        className={cn('cursor-pointer text-sm md:text-xs', className)}
        onClick={(e) => {
          handleClick('dislike')(e);
        }}
        disabled={loading}
      >
        <span className="mr-2 shrink-0 text-lg md:text-base">
          {vote === 'dislike' ? <BaseFillThumbdown /> : <BaseLineThumbdown />}
        </span>
        {t('creation.action.disLike')}
      </DropdownMenuItem>
    </>
  );
};
